import {
	bumpClientEpoch,
	readStoredSelectedClient,
	type StoredSelectedClient,
	writeStoredSelectedClient,
} from "Shared/Client/selectedClientStorage";
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";

/**
 * Outcome of a guard prompt. `"proceed"` means the client change is committed;
 * `"cancel"` means the previously-active client stays selected and the user
 * stays on the workspace.
 */
export type ClientSwitchGuardResult = "proceed" | "cancel";

/**
 * Pause hook invoked before each {@link setClient}/{@link clearClient}. Used by
 * anonymous-draft workspaces to surface a "save under client?" dialog when the
 * user switches the top-bar client mid-session. `next` is the candidate
 * selection (or `null` for a clear).
 */
export type ClientSwitchGuard = (
	next: StoredSelectedClient | null,
) => Promise<ClientSwitchGuardResult>;

type SelectedClientContextValue = {
	client: StoredSelectedClient | null;
	setClient: (client: StoredSelectedClient) => void;
	clearClient: () => void;
	/**
	 * Register a guard that will be consulted before every subsequent
	 * {@link setClient}/{@link clearClient} until the returned cleanup is called.
	 * Only one guard can be active at a time — a workspace overrides any previous
	 * one and restores it on unmount.
	 */
	registerSwitchGuard: (guard: ClientSwitchGuard) => () => void;
};

const SelectedClientContext = createContext<SelectedClientContextValue | null>(
	null,
);

export const SelectedClientProvider = ({ children }: PropsWithChildren) => {
	const [client, setClientState] = useState<StoredSelectedClient | null>(() =>
		readStoredSelectedClient(),
	);
	const guardRef = useRef<ClientSwitchGuard | null>(null);
	// True while a guard prompt is awaiting the user's decision. Blocks a second
	// switch from opening a competing prompt (which would capture a now-stale
	// `prev` client and could bump the wrong epoch / commit the wrong client).
	const guardInFlightRef = useRef(false);

	const applyClient = useCallback(
		(prev: StoredSelectedClient | null, next: StoredSelectedClient) => {
			// Crossing a client boundary (away from a previously-selected client)
			// bumps that client's epoch so workspace pages know to fork a new
			// version of the lineage on the next mutation. Selecting the same
			// client is a no-op.
			if (prev !== null && prev.sabClientID !== next.sabClientID) {
				bumpClientEpoch(prev.sabClientID);
			}
			writeStoredSelectedClient(next);
			setClientState(next);
		},
		[],
	);

	const applyClear = useCallback((prev: StoredSelectedClient | null) => {
		if (prev !== null) {
			bumpClientEpoch(prev.sabClientID);
		}
		writeStoredSelectedClient(null);
		setClientState(null);
	}, []);

	const setClient = useCallback(
		(next: StoredSelectedClient) => {
			const guard = guardRef.current;
			if (guard !== null) {
				if (guardInFlightRef.current) return;
				guardInFlightRef.current = true;
				void guard(next)
					.then((result) => {
						if (result === "proceed") {
							applyClient(client, next);
						}
					})
					.finally(() => {
						guardInFlightRef.current = false;
					});
				return;
			}
			applyClient(client, next);
		},
		[applyClient, client],
	);

	const clearClient = useCallback(() => {
		const guard = guardRef.current;
		if (guard !== null) {
			if (guardInFlightRef.current) return;
			guardInFlightRef.current = true;
			void guard(null)
				.then((result) => {
					if (result === "proceed") {
						applyClear(client);
					}
				})
				.finally(() => {
					guardInFlightRef.current = false;
				});
			return;
		}
		applyClear(client);
	}, [applyClear, client]);

	const registerSwitchGuard = useCallback((guard: ClientSwitchGuard) => {
		const previous = guardRef.current;
		guardRef.current = guard;
		return () => {
			// Only clear if no later guard replaced this one.
			if (guardRef.current === guard) {
				guardRef.current = previous;
			}
		};
	}, []);

	const value: SelectedClientContextValue = {
		client,
		setClient,
		clearClient,
		registerSwitchGuard,
	};

	return (
		<SelectedClientContext.Provider value={value}>
			{children}
		</SelectedClientContext.Provider>
	);
};

export const useSelectedClient = (): SelectedClientContextValue => {
	const value = useContext(SelectedClientContext);
	if (value === null) {
		throw new Error(
			"useSelectedClient must be used inside <SelectedClientProvider>",
		);
	}
	return value;
};
